import { LockKeyhole } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { licenseDisplayStatus } from "@/lib/license-display-status";
import { cn } from "@/lib/utils";
import type { LicenseStatus } from "@/lib/types";

export function LicenseStatusBadge({
  status,
  validUntil,
}: {
  status: LicenseStatus | string;
  validUntil?: string | null;
}) {
  const displayStatus = licenseDisplayStatus(status, validUntil);
  const variant: "success" | "warning" | "danger" | "neutral" = displayStatus === "active" ? "success" : displayStatus === "suspended" ? "warning" : displayStatus === "revoked" || displayStatus === "expired" ? "danger" : "neutral";
  return <Badge variant={variant} indicator>{displayStatus === "expired" ? "Expired" : displayStatus}</Badge>;
}

export function ActivationStatusBadge({ status }: { status: string }) {
  const variant: "success" | "danger" | "neutral" = status === "active" ? "success" : status === "revoked" ? "danger" : "neutral";
  return <Badge variant={variant} indicator>{status}</Badge>;
}

export function StateDot({ status }: { status: LicenseStatus | string }) {
  return (
    <span
      className={cn(
        "inline-block size-1.5 rounded-full",
        status === "active" && "bg-success",
        status === "suspended" && "bg-warning",
        status === "revoked" && "bg-danger",
        status === "expired" && "bg-muted",
        !["active", "suspended", "revoked", "expired"].includes(status) && "bg-muted",
      )}
      aria-hidden="true"
    />
  );
}

export function PermissionHint({ role }: { role: string; action?: string }) {
  const { t } = useTranslation();
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs text-muted"
      title={t("common.requiredRole", { role, defaultValue: `${role} role required` })}
    >
      <LockKeyhole className="size-3" aria-hidden="true" />
      {t("common.requiredRole", { role, defaultValue: `${role} required` })}
    </span>
  );
}
